/**
 * PaperTradesMonitor — open PAPER chart trades (Trade.isSimulation) as a blended
 * sub-section of the Dashboard's Trades Management panel, the same way open PAPER
 * option baskets are shown: a "N paper · hide/show" chip (persisted), a separate
 * Paper P&L, and terse rows with Chart + Close.
 *
 * Kept OUT of the real trades table on purpose: the real Open P&L, Open Risk,
 * "positions without SL" and the square-off buttons all read that table, and
 * paper must never count as real money.
 */
import React, { useMemo, useState } from 'react';
import { Box, Chip, Tooltip, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAppTheme } from '../../hooks/useAppTheme';
import { usePersistedState } from '../../hooks/usePersistedState';
import fmt from '../../utils/priceFormat';
import OpenChartButton from './OpenChartButton';
import SlModeChip, { SlModeInfo } from './SlModeChip';

export interface PaperTradeRow {
  id: number;
  tradeId?: string;
  symbol?: string;
  exchange?: string;
  side: 'LONG' | 'SHORT' | string;
  quantity: number;
  averageEntryPrice: number;
  currentPrice?: number | null;
  unrealizedPnl?: number | null;
  stopLoss?: number | null;
  manualStopLoss?: number | null;
  target?: number | null;
  decimalPrecision?: number;
  instrument?: { exchange?: string; decimalPrecision?: number; tickSize?: number } | null;
  originIntervalMinutes?: number;
  createdAt?: string;
  slModeInfo?: SlModeInfo | null;
}

const num = (v: any): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const signedInr = (v: number) => `${v >= 0 ? '+' : '−'}${fmt.inr(Math.abs(v), 0)}`;

interface Props {
  trades: PaperTradeRow[];
  onOpenChart: (t: PaperTradeRow) => void;
  onChanged: () => void;
}

const PaperTradesMonitor: React.FC<Props> = ({ trades, onOpenChart, onChanged }) => {
  const { C } = useAppTheme();
  const [show, setShow] = usePersistedState<boolean>('dashboard.paperTrades.show.v1', true);
  const [closeTarget, setCloseTarget] = useState<PaperTradeRow | null>(null);
  const [closing, setClosing] = useState(false);

  const total = useMemo(() => trades.reduce((s, t) => s + (num(t.unrealizedPnl) || 0), 0), [trades]);
  if (!trades.length) return null; // nothing open — keep the dashboard clean

  const doClose = async () => {
    if (!closeTarget) return;
    setClosing(true);
    try {
      const r: any = await api.post(`/trades/${closeTarget.id}/close`, {});
      const pnl = num(r?.data?.data?.realizedPnl);
      toast.success(`PAPER ${closeTarget.symbol} closed${pnl != null ? ` · ${signedInr(pnl)}` : ''}`);
      setCloseTarget(null);
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Could not close the paper trade');
    } finally {
      setClosing(false);
    }
  };

  return (
    <Box data-testid="dash-paper-trades"
      sx={{ mb: 2, p: 1.25, borderRadius: 1.5, borderLeft: '3px solid #546e7a', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(84,110,122,0.14)' : 'rgba(84,110,122,0.07)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: show ? 1 : 0, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Paper trades</Typography>
        <Tooltip arrow title={show ? 'Hide open PAPER chart trades from the Dashboard (they stay on the Trades page and the chart)' : 'Show open PAPER chart trades here'}>
          <Chip size="small" data-testid="dash-paper-trades-toggle" onClick={() => setShow(v => !v)}
            label={show ? `${trades.length} paper · hide` : `${trades.length} paper hidden · show`}
            variant={show ? 'filled' : 'outlined'}
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', bgcolor: show ? '#546e7a' : 'transparent', color: show ? '#fff' : 'text.secondary', borderColor: '#546e7a' }} />
        </Tooltip>
        <Box sx={{ ml: 'auto' }}>
          <Tooltip arrow title="Simulated — not part of your real Open P&L, the loss cap or the reports.">
            <Chip size="small" data-testid="dash-paper-trades-pnl" label={`Paper P&L ${signedInr(total)}`} variant="outlined"
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary' }} />
          </Tooltip>
        </Box>
      </Box>

      {show && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {trades.map(t => {
            const instr = { exchange: t.exchange || t.instrument?.exchange, decimalPrecision: t.decimalPrecision ?? t.instrument?.decimalPrecision, tickSize: t.instrument?.tickSize };
            const pnl = num(t.unrealizedPnl) || 0;
            const sl = num(t.manualStopLoss) ?? num(t.stopLoss);
            const tgt = num(t.target);
            const isLong = String(t.side).toUpperCase() === 'LONG';
            const cell = (label: string, value: React.ReactNode, color?: string) => (
              <Box sx={{ minWidth: 72 }}>
                <Typography component="div" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.1 }}>{label}</Typography>
                <Typography component="div" sx={{ fontSize: '0.78rem', fontWeight: 600, color, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
              </Box>
            );
            return (
              <Box key={t.id} data-testid="dash-paper-trade-row"
                sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: 1, py: 0.6, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 150 }}>
                  <Chip size="small" label="PAPER" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800, bgcolor: '#546e7a', color: '#fff' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{t.symbol}</Typography>
                  <Chip size="small" label={isLong ? 'LONG' : 'SHORT'}
                    sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800, bgcolor: isLong ? C.bullMuted : C.bearMuted, color: isLong ? C.bull : C.bear }} />
                </Box>
                {cell('Qty', fmt.qty(num(t.quantity), instr))}
                {cell('Entry', fmt.price(num(t.averageEntryPrice), instr))}
                {cell('LTP', fmt.price(num(t.currentPrice), instr))}
                {cell('SL', sl != null ? fmt.price(sl, instr) : '—', C.bear)}
                {cell('Target', tgt != null ? fmt.price(tgt, instr) : '—', C.bull)}
                {cell('P&L', signedInr(pnl), pnl >= 0 ? C.bull : C.bear)}
                <Box sx={{ minWidth: 72 }}>
                  <Typography component="div" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.1 }}>Stop rule</Typography>
                  <SlModeChip tradeId={t.id} info={t.slModeInfo ? { ...t.slModeInfo, backupStopEnabled: false } : null} ownTf={t.originIntervalMinutes} instrument={instr} onChanged={onChanged} />
                </Box>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <OpenChartButton onOpen={() => onOpenChart(t)} title="Open the chart (drag the SL / target there)" />
                  <Tooltip arrow title="Close this PAPER trade at the live price (simulated — no broker order)">
                    <IconButton size="small" data-testid="dash-paper-trade-close" onClick={() => setCloseTarget(t)} sx={{ color: C.bear }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog open={!!closeTarget} onClose={() => !closing && setCloseTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Close PAPER trade?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Closes the simulated {closeTarget?.side} on <strong>{closeTarget?.symbol}</strong> at the live price. No broker order is placed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseTarget(null)} disabled={closing}>Cancel</Button>
          <Button onClick={doClose} disabled={closing} variant="contained" sx={{ bgcolor: '#546e7a', color: '#fff', '&:hover': { bgcolor: '#455a64' } }}>
            {closing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Close paper trade'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaperTradesMonitor;
